/**
 * 🏗️ Layout Component
 * Haupt-Layout mit Navigation
 */

import React, { useState } from 'react';
import { Mail, Menu, X, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../services/AuthService';
import { useTheme } from '../../context/ThemeContext';  // 🌙 Dark-Mode

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();  // 🌙 Dark-Mode
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-primary transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-dark-secondary shadow-sm border-b dark:border-dark-copper-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center font-bai-jamjuree">
              <Mail className="h-5 w-5 md:h-6 md:w-6 mr-2 text-blue-600 dark:text-blue-500" />
                <span className="hidden sm:inline">iSENDINBOX</span>
                <span className="sm:hidden">iSB</span>
              </h1>
            <span className="hidden md:block ml-4 text-sm text-gray-500 dark:text-gray-400">
                Angemeldet als: <span className="font-medium text-gray-700 dark:text-gray-300">{user?.username}</span>
                {user?.telegram_user_id && (
                  <span 
                    className="ml-2 text-xs text-gray-400 dark:text-gray-500 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(user.telegram_user_id);
                      const toast = document.createElement('div');
                      toast.textContent = 'User ID kopiert!';
                      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50';
                      document.body.appendChild(toast);
                      setTimeout(() => toast.remove(), 2000);
                    }}
                    title="Klicken zum Kopieren"
                  >
                    (UserID: {user.telegram_user_id})
                  </span>
                )}
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <nav className="flex space-x-6">
                <a href="/" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">Dashboard</a>
                <a href="/smtp" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">SMTP</a>
                <a href="/webmailer" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">Webmailer</a>
                <a href="/templates" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">Templates</a>
                <a href="/jobs" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">Jobs</a>
                <a href="/cid-logos" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">CID Logos</a>
                {user?.role === 'admin' && (
                  <a href="/admin/users" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 font-medium">Admin</a>
                )}
              </nav>
              <div className="flex items-center space-x-3">
                {/* 🌙 Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors border border-transparent dark:border-dark-copper-mid"
                  title={darkMode ? 'Light Mode' : 'Dark Mode'}
                >
                  {darkMode ? (
                    <Sun className="h-5 w-5 text-dark-copper-glow" />
                  ) : (
                    <Moon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
                
                <a
                  href="/?create=true"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Neuer Job
                </a>
                <button
                  onClick={logout}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Abmelden
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors"
              >
                {darkMode ? (
                  <Sun className="h-5 w-5 text-dark-copper-glow" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-600" />
                )}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 p-2"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 dark:border-dark-copper-dark py-4 bg-white dark:bg-dark-secondary">
              <div className="flex flex-col space-y-4">
                <div className="text-sm text-gray-500 dark:text-gray-400 px-4">
                  Angemeldet als: <span className="font-medium text-gray-700 dark:text-gray-300">{user?.username}</span>
                  {user?.telegram_user_id && (
                    <div 
                      className="text-xs text-gray-400 dark:text-gray-500 mt-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(user.telegram_user_id);
                        const toast = document.createElement('div');
                        toast.textContent = 'User ID kopiert!';
                        toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50';
                        document.body.appendChild(toast);
                        setTimeout(() => toast.remove(), 2000);
                      }}
                    >
                      UserID: {user.telegram_user_id} 📋
                    </div>
                  )}
                </div>
                <nav className="flex flex-col space-y-2 px-4">
                  <a 
                    href="/" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    📊 Dashboard
                  </a>
                  <a 
                    href="/smtp" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    📧 SMTP Server
                  </a>
                  <a 
                    href="/webmailer" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Webmailer
                  </a>
                  <a 
                    href="/templates" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    📝 Templates
                  </a>
                  <a 
                    href="/jobs" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    🚀 Jobs
                  </a>
                  <a 
                    href="/cid-logos" 
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    🎨 CID Logos
                  </a>
                  {user?.role === 'admin' && (
                    <a
                      href="/admin/users"
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium py-2 border-b border-gray-100 dark:border-dark-copper-dark"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      👑 Admin
                    </a>
                  )}
                </nav>
                <div className="px-4 pt-2 space-y-3">
                  <a
                    href="/?create=true"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Neuer Job
                  </a>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Abmelden
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
